import { useState, useEffect } from "react";
import { Button, Box, Container } from "@mui/material";
import { useTranslation, Trans } from "react-i18next";
import { DJSimpleCodeBlock } from "../../../main/components/codeblock";
import {
  IProgress,
  ISessionProps,
  OSType,
} from "../../../main/components/IProgress";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography } from "@appsflyer/fe-ui-core";
import transition from "../../../main/components/transition";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import { amplitudeTrack_continue } from "../../../services/utils";
import AmplitudeLink from "../../../services/AmplitudeLink";

const InstallSDK = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [progress, setProgress] = useState<IProgress[]>([]);
  const [activeStep, setActiveStep] = useState(0);

  // Determine the platform using location.state?.os (available throughout component)
  const isIOS = location.state?.os === OSType.ReactNativeIOS;
  const isAndroid = location.state?.os === OSType.ReactNativeAndroid;

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  useEffect(() => {
    const state: ISessionProps = location.state as ISessionProps;
    setProgress(state?.progress);
  }, [location.state]);

  const handleClick = () => {
    const progressArr: IProgress[] = progress.slice();
    
    if (isIOS) {
      // For iOS, add "Start SDK" progress item
      progressArr.push({
        id: "reactnative_installSDK",
        text: "progress_startsdk_startSDK",
        order: progress.length + 1,
        startTime: new Date(),
        updateTime: new Date(),
      });
    } else if (isAndroid) {
      // For Android, add "Android Manifest Configuration" progress item
      progressArr.push({
        id: "reactnative_installSDK",
        text: "Android Manifest Configuration",
        order: progress.length + 1,
        startTime: new Date(),
        updateTime: new Date(),
      });
    }
    
    // Initialize progress with a copy of progressArr
    const progressState: ISessionProps = {
      ...location.state,
      progress: progressArr,
      currentPage: location.state?.currentPage + 1,
    };

    amplitudeTrack_continue(location.state);

    // Navigate based on platform
    if (isIOS) {
      // For iOS, navigate to StartSDK first (before testsdktype)
      navigate(`/startsdk/reactnative_ios/startsdk`, {
        state: progressState,
      });
    } else if (isAndroid) {
      // For Android, continue to step 6
      navigate(`/startsdk/reactnative_android/installSDK_step6`, {
        state: progressState,
      });
    }
  };

  // Build steps array conditionally based on platform
  const baseSteps = [
    {
      label: t("StartSDK_installSDK_reactnative_InstallExpoDevClient"),
      component: (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t("StartSDK_installSDK_reactnative_InstallExpoDevClient_description")}{" "}
              <AmplitudeLink
                href="https://docs.expo.dev/development/introduction/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("StartSDK_startSDK_ios_step3_ATT_tooltip_d")}
              </AmplitudeLink>
            </Typography>
            <DJSimpleCodeBlock
              codePrimary={"expo install expo-dev-client"}
              showLineNumbers={false}
              language="bash"
            />
          </Box>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
    {
      label: t("StartSDK_installSDK_reactnative_InstallReactNativeAppsFlyer"),
      component: (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t("StartSDK_installSDK_reactnative_InstallReactNativeAppsFlyer_description")}
            </Typography>
            <DJSimpleCodeBlock
              codePrimary={"expo install react-native-appsflyer"}
              showLineNumbers={false}
              language="bash"
            />
          </Box>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
    {
      label: t("StartSDK_installSDK_reactnative_ConfigureAppJson"),
      component: (
        <>
          <Box sx={{ mb: 4 }}>
            {/* <Typography variant="h3" sx={{ mb: 2 }}>
              Add AppsFlyer Plugin to app.json
            </Typography> */}
            <Typography variant="body1" sx={{ mb: 2 }}>
              <Trans 
                i18nKey="StartSDK_installSDK_reactnative_ConfigureAppJson_description"
                components={{ code: <code /> }}
              />
            </Typography>
            <DJSimpleCodeBlock
              codePrimary={`...
    "plugins": [
        [
            "react-native-appsflyer",
            {}
        ]
    ],
...`}
              showLineNumbers={false}
              language="json"
            />
          </Box>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
    {
      label: `${t("startSDK_ReferrerPage_android_Optional")} ${t("StartSDK_installSDK_reactnative_Purchase_Connector")}`,
      component: (
        <>
          <Box sx={{ mb: 4 }}>
             <Typography variant="body1" sx={{ mb: 2 }}>
               <Trans 
                 i18nKey="StartSDK_installSDK_reactnative_Purchase_Connector_description"
                 components={{ code: <code /> }}
               />{" "}
               {isAndroid ? (
                 <Trans
                  i18nKey="StartSDK_installSDK_reactnative_Purchase_Connector_description_android"
                  components={{ code: <code /> }}               
                 />
               ) : (
                <Trans
                  i18nKey="StartSDK_installSDK_reactnative_Purchase_Connector_description_ios"
                  components={{ code: <code /> }}               
               />
               )}
             </Typography>
             <DJSimpleCodeBlock
              codePrimary={`...
"plugins": [
      [
        "react-native-appsflyer",
        {
          "shouldUsePurchaseConnector": true
        }
      ]
    ],
...`}
              showLineNumbers={false}
              language="json"
             />
          </Box>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
    {
      label: `${t("startSDK_ReferrerPage_android_Optional")} ${t("StartSDK_installSDK_reactnative_Strict_Mode_for_Kids_Apps")}`,
      component: (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}> 
              <Trans
                i18nKey="StartSDK_installSDK_reactnative_Strict_Mode_for_Kids_Apps_description"
                components={{ code: <code /> }}
              />
            </Typography>
            <DJSimpleCodeBlock
              codePrimary={`...
"plugins": [
      [
        "react-native-appsflyer",
        {
          "shouldUseStrictMode": true
        }
      ]
    ],
...`}
              showLineNumbers={false}
              language="json"
            />
          </Box>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
  ];

  // Add Android-specific Backup Rules step only for Android
  const backupRulesStep = isAndroid ? [
    {
      label: `${t("startSDK_ReferrerPage_android_Optional")} ${t("StartSDK_installSDK_reactnative_Backup_Rules_Configuration")}`,
      component: (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t("StartSDK_installSDK_reactnative_Backup_Rules_Configuration_description1")}{" "}
              {t("StartSDK_installSDK_reactnative_Backup_Rules_Configuration_description2")}
            </Typography>
            <Typography variant="h3" sx={{ mb: 2 }}>
              <Trans
                i18nKey="StartSDK_installSDK_reactnative_Backup_Rules_Configuration_description3"
                components={{ code: <code /> }}
              />
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                <Trans
                  i18nKey="StartSDK_installSDK_reactnative_Backup_Rules_Configuration_description4"
                  components={{ code: <code /> }}
                />
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                {t("StartSDK_installSDK_reactnative_Backup_Rules_Configuration_description5")}
              </Typography>
              <Typography component="li" variant="body1">
                {t("StartSDK_installSDK_reactnative_Backup_Rules_Configuration_description6")}
              </Typography>
            </Box>
            <Typography variant="h3" sx={{ mb: 2, mt: 3 }}>
              <Trans
                i18nKey="StartSDK_installSDK_reactnative_Backup_Rules_Configuration_description7"
                components={{ code: <code /> }}
              />
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                {t("StartSDK_installSDK_reactnative_Backup_Rules_Configuration_description8")}
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                {t("StartSDK_installSDK_reactnative_Backup_Rules_Configuration_description9")}
              </Typography>
              <Typography component="li" variant="body1">
                {t("StartSDK_installSDK_reactnative_Backup_Rules_Configuration_description10")}
              </Typography>
            </Box>
            <Typography variant="h3" sx={{ mb: 2, mt: 3 }}>
              <Trans
                i18nKey="StartSDK_installSDK_reactnative_Backup_Rules_Configuration_description11"
                components={{ code: <code /> }}
              />
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                {t("StartSDK_installSDK_reactnative_Backup_Rules_Configuration_description12")}
              </Typography>
              <Typography component="li" variant="body1" sx={{ mb: 1 }}>
                {t("StartSDK_installSDK_reactnative_Backup_Rules_Configuration_description13")}
              </Typography>
              <Typography component="li" variant="body1">
                {t("StartSDK_installSDK_reactnative_Backup_Rules_Configuration_description14")}
              </Typography>
            </Box>
            <DJSimpleCodeBlock
              codePrimary={`...
"plugins": [
  [
    "react-native-appsflyer",
    {
      "preferAppsFlyerBackupRules": true
    }
  ]
],
...`}
              showLineNumbers={false}
              language="json"
            />
          </Box>
        </>
      ),
      shouldDisplayNavigationButtons: true,
    },
  ] : [];

  const steps = [
    ...baseSteps,
    ...backupRulesStep,
  ];

  return (
    <>
      <Container maxWidth={"lg"}>
        <Box padding={3} style={{ minHeight: "50vh" }}>
          <Typography variant="h1">
            {t("progress_start_installSDK")}
          </Typography>

          <Stepper
            activeStep={activeStep}
            style={{ border: "none" }}
            orientation="vertical"
          >
            {steps.map(
              ({ label, component, shouldDisplayNavigationButtons }) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                  <StepContent>
                    {component}
                    {shouldDisplayNavigationButtons && (
                      <Box sx={{ mt: 2 }}>
                        {activeStep !== 0 && (
                          <Button
                            variant="text"
                            color="primary"
                            onClick={handleBack}
                          >
                            {t("general_Back")}
                          </Button>
                        )}
                        {activeStep !== steps.length - 1 && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleNext}
                          >
                            {activeStep === steps.length - 1
                              ? "Finish"
                              : t("general_Next")}
                          </Button>
                        )}
                      </Box>
                    )}
                  </StepContent>
                </Step>
              )
            )}
          </Stepper>
        </Box>
        <Box
          display={"flex"}
          width={"100%"}
          justifyContent="space-between"
          alignSelf="end"
          justifySelf="end"
          marginTop={10}
          textAlign="end"
        >
          {" "}
          <Button
            variant="outlined"
            size="medium"
            color="secondary"
            style={{ marginRight: "5px" }}
            onClick={() => navigate(-1)}
          >
            {t("general_Back")}
          </Button>
          <Button
            variant="contained"
            size="medium"
            color="primary"
            onClick={() => handleClick()}
          >
            {t("general_Continue")}
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default transition(InstallSDK);

